import { useState, useEffect, useCallback } from 'react';
import { getLocalStorage } from '../../utils/safeJson';

/**
 * Custom hook to manage saved filter views
 * @param {string} savedViewsKey - localStorage key for saved views
 * @param {function} onChange - Callback when a view is loaded
 * @returns {object} - Saved views state and handlers
 */
export default function useSavedViews(savedViewsKey = 'associationsSavedViews', onChange) {
  const [savedViews, setSavedViews] = useState(() => {
    // First try the page-specific key
    const pageSpecific = getLocalStorage(savedViewsKey, null);
    if (pageSpecific) {
      return pageSpecific;
    }

    // If no page-specific views and this is not the default key, migrate from default
    if (savedViewsKey !== 'associationsSavedViews') {
      const defaultViews = getLocalStorage('associationsSavedViews', null);
      if (defaultViews) {
        localStorage.setItem(savedViewsKey, JSON.stringify(defaultViews));
        return defaultViews;
      }
    }

    return {};
  });

  const [activeViewName, setActiveViewName] = useState('Custom');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, name: null });

  const persistViews = useCallback((views) => {
    setSavedViews(views);
    localStorage.setItem(savedViewsKey, JSON.stringify(views));
  }, [savedViewsKey]);

  // Initialize default funnel stage filters if none exist
  const initializeDefaultFunnelFilters = useCallback(() => {
    if (Object.keys(savedViews).length === 0) {
      const defaultFilters = {
        'Active Pipeline': {
          statuses: ['New', 'Attempt to Contact', 'Engaged', 'Discovery', 'Proposal', 'Refinement', 'Contract Sent'],
          funnelStages: ['Early Stage', 'Discovery', 'Proposal', 'Contract'],
          strategicAccounts: [],
          leadTypes: [],
          strategies: [],
          text: '',
          lastUpdatedDays: null,
          lastEngagedDays: null,
          lastCreatedDays: null,
          sizeBuckets: [],
          duplicatesOnly: false,
        },
        'Strategic Accounts': {
          statuses: ['New', 'Attempt to Contact', 'Engaged', 'Discovery', 'Proposal', 'Refinement', 'Contract Sent'],
          strategicAccounts: ['Yes'],
          funnelStages: [],
          leadTypes: [],
          strategies: [],
          text: '',
          lastUpdatedDays: null,
          lastEngagedDays: null,
          lastCreatedDays: null,
          sizeBuckets: [],
          duplicatesOnly: false,
          legacyLeadsOnly: false,
        },
        'Hot Prospects': {
          statuses: ['Discovery', 'Proposal', 'Refinement'],
          funnelStages: ['Discovery', 'Proposal'],
          strategicAccounts: [],
          leadTypes: [],
          strategies: [],
          text: '',
          lastUpdatedDays: null,
          lastEngagedDays: null,
          lastCreatedDays: null,
          sizeBuckets: [],
          duplicatesOnly: false,
          legacyLeadsOnly: false,
        },
      };
      persistViews(defaultFilters);
    }
  }, [savedViews, persistViews]);

  // Initialize default filters on component mount
  useEffect(() => {
    if (savedViewsKey === 'associationsSavedViews') {
      initializeDefaultFunnelFilters();
    }
  }, [savedViewsKey, initializeDefaultFunnelFilters]);

  const saveCurrentView = useCallback((safeValue) => {
    const name = prompt('Name for this view?');
    if (!name) return;
    const serialisable = {
      statuses: Array.from(safeValue.statuses || []),
      leadTypes: Array.from(safeValue.leadTypes || []),
      strategies: Array.from(safeValue.strategies || []),
      sizeBuckets: Array.from(safeValue.sizeBuckets || []),
      associationTypes: Array.from(safeValue.associationTypes || []),
      selfManaged: Array.from(safeValue.selfManaged || []),
      text: safeValue.text || '',
      lastUpdatedDays: safeValue.lastUpdatedDays ?? null,
      lastEngagedDays: safeValue.lastEngagedDays ?? null,
      lastCreatedDays: safeValue.lastCreatedDays ?? null,
      duplicatesOnly: !!safeValue.duplicatesOnly,
      stalledOnly: !!safeValue.stalledOnly,
    };
    persistViews({ ...savedViews, [name]: serialisable });
  }, [savedViews, persistViews]);

  const loadView = useCallback((name) => {
    const view = savedViews[name];
    if (!view || !onChange) return;
    onChange({
      statuses: new Set(view.statuses),
      leadTypes: new Set(view.leadTypes),
      strategies: new Set(view.strategies),
      sizeBuckets: new Set(view.sizeBuckets),
      associationTypes: new Set(view.associationTypes || []),
      selfManaged: new Set(view.selfManaged || []),
      text: view.text,
      lastUpdatedDays: view.lastUpdatedDays,
      lastEngagedDays: view.lastEngagedDays,
      lastCreatedDays: view.lastCreatedDays,
      duplicatesOnly: !!view.duplicatesOnly,
      legacyLeadsOnly: !!view.legacyLeadsOnly,
      stalledOnly: !!view.stalledOnly,
    });
  }, [savedViews, onChange]);

  const deleteView = useCallback((name) => {
    setConfirmDelete({ open: true, name });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmDelete.name) return;
    const clone = { ...savedViews };
    delete clone[confirmDelete.name];
    persistViews(clone);
    if (activeViewName === confirmDelete.name) {
      setActiveViewName('Custom');
    }
    setConfirmDelete({ open: false, name: null });
  }, [confirmDelete.name, savedViews, activeViewName, persistViews]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDelete({ open: false, name: null });
  }, []);

  return {
    savedViews,
    activeViewName,
    setActiveViewName,
    confirmDelete,
    saveCurrentView,
    loadView,
    deleteView,
    handleConfirmDelete,
    handleCancelDelete,
  };
}
